import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'

import { IQuestion } from 'cat-shared/utils'
import { AppDivider, AppLoader } from 'client/ui-building-blocks'
import { startQuestionSocket, stopQuestionSocket } from 'client/services'
import { QuestionSubmitFormValues } from 'client/utils'
import { useAppContext } from 'client/hooks'

import {
  QuestionMeta,
  QuestionVoteResults,
  QuestionSubmitForm,
} from 'client/components/Question'

import ReportQuestionModal from './ReportQuestionModal/report-question-modal'
import DeleteQuestionModal from './DeleteQuestionModal/delete-question-modal'
import SubmitQuestionModal from './SubmitQuestionModal/submit-question-modal'

type Props = {
  question?: IQuestion
  previewMode?: boolean
  isBeingFetched?: boolean
  isBeingSubmitted?: boolean
  areResultsBeingLoaded?: boolean
  handleQuestionSubmission?: (formValues: QuestionSubmitFormValues) => void
}

const Question = ({
  question = null,
  previewMode = false,
  isBeingFetched = false,
  isBeingSubmitted = false,
  areResultsBeingLoaded = false,
  handleQuestionSubmission = null,
}: Props) => {
  const queryClient = useQueryClient()

  const { authUser } = useAppContext()

  // managing sockets
  useEffect(() => {
    if (!previewMode) {
      if (question?.answeredStats && !isEmpty(question?.answeredStats)) {
        startQuestionSocket(queryClient, question._id.toString())
      } else {
        stopQuestionSocket()
      }
    }
  }, [previewMode, queryClient, question?._id, question?.answeredStats])

  if (previewMode) {
    return (
      <>
        <AppDivider size="xsmall" />
        <QuestionMeta question={question} />
        <AppDivider size="xsmall" />
        <QuestionSubmitForm question={question} previewMode />
      </>
    )
  } else {
    const questionId = question?._id?.toString() || null

    const hasBeenAnswered =
      authUser && question && question?.answeredStats?.byRequestor.length > 0

    return (
      <AppLoader active={!questionId || isBeingFetched}>
        <div>
          <AppDivider size="xsmall" />
          <QuestionMeta
            question={question}
            areResultsBeingLoaded={areResultsBeingLoaded}
          />
          <AppDivider size="small" />
          <AppLoader active={areResultsBeingLoaded}>
            {hasBeenAnswered ? (
              <QuestionVoteResults question={question} />
            ) : (
              <QuestionSubmitForm
                question={question}
                previewMode={previewMode}
                isBeingSubmitted={isBeingSubmitted}
                handleQuestionSubmission={handleQuestionSubmission}
              />
            )}
          </AppLoader>
          <ReportQuestionModal questionId={questionId} />
          <DeleteQuestionModal questionId={questionId} />
          <SubmitQuestionModal />
        </div>
      </AppLoader>
    )
  }
}

export default Question
